import { sumMoney } from '@/features/workspace-usage';

/**
 * What a cost is in when no run says otherwise — which only happens for a task with no
 * runs, where no amount is reported anyway. Matches the contract's own default.
 */
const DEFAULT_CURRENCY = 'USD';

import { STATUS_PRESENTATION } from './statusPresentation';
import { latestExecution } from './executionStatus';
import type { CardExecutionState } from './executionStatus';
import type {
  TaskExecutionStatus,
  TaskExecutionSummary,
  TaskView,
} from '@/lib/api/generated/model';

const SECOND = 1000;
const MINUTE_IN_SECONDS = 60;
const HOUR_IN_MINUTES = 60;

/** The one currency formatter for a run's cost — shared so every display of it agrees on precision. */
export const COST_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/**
 * How long a run took, or `null` when that was not recorded.
 *
 * Never estimated from `created_at` or from the clock: a run that has started
 * and not ended has no duration yet, and inventing "running for 4s" would put a
 * number on the screen the service never reported.
 */
export function runDuration(
  execution: Pick<TaskExecutionSummary, 'started_at' | 'ended_at'>,
): string | null {
  const { started_at, ended_at } = execution;
  if (!started_at || !ended_at) return null;

  const elapsed = new Date(ended_at).getTime() - new Date(started_at).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return null;

  return formatDurationMs(elapsed);
}

/**
 * A millisecond span as `4s`, `1m 30s` or `1h 59m` — the same units
 * `runDuration` prints, shared so a second span (e.g. a transcript entry's own
 * `duration_ms`) reads in the same vocabulary rather than growing its own
 * rounding rules.
 *
 * Rounded once, to whole seconds, and only then split into units. Rounding each
 * unit on its own is how you get `60s`, `2m 60s` and `1h 60m` — 59.7 seconds
 * rounds to 60 while still being under a minute, so it never rolls over into
 * the next unit.
 */
export function formatDurationMs(elapsedMs: number): string {
  const totalSeconds = Math.round(elapsedMs / SECOND);
  if (totalSeconds < MINUTE_IN_SECONDS) return `${String(totalSeconds)}s`;

  const totalMinutes = Math.floor(totalSeconds / MINUTE_IN_SECONDS);
  if (totalMinutes < HOUR_IN_MINUTES) {
    return `${String(totalMinutes)}m ${String(totalSeconds % MINUTE_IN_SECONDS)}s`;
  }

  const hours = Math.floor(totalMinutes / HOUR_IN_MINUTES);
  return `${String(hours)}h ${String(totalMinutes % HOUR_IN_MINUTES)}m`;
}

/**
 * When this task arrived at the step it is standing on.
 *
 * Read from the newest recorded run, not from `updated_at`. A move always
 * creates an execution of the step it moves to — even the terminal one, which
 * records a `skipped` run — so the newest run's `created_at` is when the task
 * got where it is. `updated_at` carries `onupdate=now()` on the task row, so
 * it answers "when was this row last written", a different question that
 * happens to have the same answer today and would quietly stop having it the
 * first time anything else writes to a task.
 *
 * A task with no runs *of that step* has never moved onto it, so the answer is
 * when it was created — which is exactly how long it has been waiting in
 * Backlog, and the honest answer for a task the workflow has orphaned.
 */
export function stepEntryTime(task: TaskView): string {
  return (
    latestExecution(runsOfCurrentStep(task))?.created_at ?? task.created_at
  );
}

/**
 * The recorded runs of the step this task is standing on.
 *
 * Filtered by the recorded snapshot rather than taken wholesale, which for a
 * task walking the board normally is the same set — a move records a run of
 * the step it moves to, so the newest run is a run of the current step. It
 * stops being the same set for a task the workflow has orphaned: its newest
 * run belongs to a step that no longer exists, and reading it would date the
 * task's arrival from somewhere it is no longer standing.
 */
function runsOfCurrentStep(task: TaskView): TaskExecutionSummary[] {
  return (task.executions ?? []).filter(
    (execution) => execution.step_id_snapshot === task.position,
  );
}

/**
 * How many times the step this task is standing on has been run.
 *
 * Counted against the current position rather than over the whole history,
 * because the number worth showing is "this step was attempted more than
 * once", not "this task has done several things". A task that walked cleanly
 * through four steps has four runs and has retried nothing.
 */
export function runsOnCurrentStep(task: TaskView): number {
  return runsOfCurrentStep(task).length;
}

/**
 * What this task has cost — and whether that can be stated at all (spec 089).
 *
 * Three outcomes, not a number, because a number cannot carry the difference between
 * "this cost nothing" and "nobody could measure what this cost". Collapsing those two
 * is what the previous implementation did, and 071 FR-012 exists because of it.
 */
export type TaskCost =
  // The currency travels with the amount rather than being fetched separately by
  // whoever renders it (073 FR-007): a figure and the unit it is in are one fact, and
  // a caller that has to go and find the second can get it wrong.
  | { kind: 'exact'; amount: string; currency: string }
  | { kind: 'atLeast'; amount: string; currency: string }
  // Runs happened and not one of them has a cost anybody can state.
  | { kind: 'unknown' }
  // Nothing has run, so there is no claim to make either way. Distinct from
  // `unknown`, which is a statement about runs that exist: saying "cost unknown" on
  // every task sitting in the backlog would put a caveat on the majority of a fresh
  // board, and the caveat is only meaningful where something was actually spent.
  | { kind: 'none' };

/**
 * A task's spend, summed over every run it has ever had — not just the current step's,
 * unlike `runsOnCurrentStep`. A task's spend does not reset when it moves on, so the
 * total follows its whole history to mean anything.
 *
 * **Where the figure comes from changed, and that is the point.** This used to reach
 * into each execution's `transcript` for `result` entries and sum their `cost`. That is
 * the one thing 071 FR-012 forbids, and the function's own docstring conceded why: a
 * missing transcript, a missing `result` entry, or a `result` with no recorded cost all
 * read as zero rather than as unknown — so the total under-counted silently, and the
 * board and the usage dashboard could disagree about money with the board reading low.
 * The figures now come from the priced ledger, per run, on the contract.
 *
 * **One unknown run makes the whole total a floor.** It does not average out among
 * enough known ones, and nine priced runs beside one unmeasured one still yield
 * `atLeast`. A floor presented as a total is the same false confidence in a politer
 * form.
 *
 * A task with no runs at all is `none`, not `unknown` and certainly not zero. Those
 * are three different statements — "nothing to say", "we cannot say", "it was free" —
 * and only the first is true of a task that has never started.
 */
export function taskCost(task: TaskView): TaskCost {
  const own = costOfRuns(task.executions ?? []);
  if (task.subtree_cost == null) return own;
  // The service's subtree total is its own runs plus every sub-workflow run; a zero with
  // no own runs is a task that never ran, or whose descendants never ran either.
  const subtreeIsZero = Number(task.subtree_cost) === 0;
  if (subtreeIsZero && task.subtree_cost_complete === false) {
    return { kind: 'unknown' };
  }
  if (own.kind === 'none' && subtreeIsZero) return own;
  const currency =
    own.kind === 'exact' || own.kind === 'atLeast'
      ? own.currency
      : DEFAULT_CURRENCY;
  return task.subtree_cost_complete === false
    ? { kind: 'atLeast', amount: task.subtree_cost, currency }
    : { kind: 'exact', amount: task.subtree_cost, currency };
}

/**
 * The same sum over any set of runs — one attempt's, on the Executions tab
 * (spec 127) — so a task's total and its attempts' totals are one arithmetic.
 */
export function costOfRuns(runs: readonly TaskExecutionSummary[]): TaskCost {
  if (runs.length === 0) return { kind: 'none' };

  const known: string[] = [];
  let anyUnknown = false;
  // Taken only from runs that contribute to the sum. Reading it from every run would
  // let a run we could not price — or the last one iterated, whichever came later —
  // name the unit of a figure it is not part of.
  let currency: string | null = null;

  for (const execution of runs) {
    // `null` is the contract's "no figure can be stated" — unmeasured, unpriced (077),
    // or not yet terminal. `'0'` is a real answer and must not be swept in with it.
    if (execution.cost == null) {
      anyUnknown = true;
      continue;
    }
    // Optional on the contract because it carries a server-side default, so an absent
    // one means the default rather than a different currency. Normalised before the
    // comparison below — otherwise a run that simply omitted it would read as a second
    // currency and turn an exact total into a floor for no reason.
    const runCurrency = execution.currency || DEFAULT_CURRENCY;
    // Runs in a second currency cannot be added to the first. Rather than pick one and
    // label a mixed sum with it, drop them the way an unstatable cost is dropped: the
    // total becomes a floor over what could honestly be added. Cannot happen while the
    // contract names one currency, and is not a reason to produce a wrong number if it
    // ever does.
    if (currency !== null && runCurrency !== currency) {
      anyUnknown = true;
      continue;
    }
    currency ??= runCurrency;
    known.push(execution.cost);
  }

  // `sumMoney` refuses the whole sum if any amount is unreadable, rather than returning
  // a total quietly missing a part. Retry without the offenders: eight priced runs and
  // one malformed one still support "at least" over the eight, and discarding a figure
  // that is true in order to report nothing is the opposite of what this is for.
  let addable = known;
  if (sumMoney(addable) === null) {
    addable = known.filter((value) => sumMoney([value]) !== null);
    anyUnknown = true;
  }

  // Nothing left to add is `unknown`, not zero. `sumMoney([])` is `'0'` — correct for a
  // sum of nothing, and a lie about a task whose every figure was unreadable.
  if (addable.length === 0) return { kind: 'unknown' };

  const amount = sumMoney(addable);
  if (amount === null) return { kind: 'unknown' };

  return anyUnknown
    ? { kind: 'atLeast', amount, currency: currency ?? DEFAULT_CURRENCY }
    : { kind: 'exact', amount, currency: currency ?? DEFAULT_CURRENCY };
}

/**
 * An input value as text, without inventing a format for it.
 *
 * Strings are shown as they are — quoting them would suggest a quoting the value
 * does not have. Everything else goes through JSON, which is the shape the value
 * arrived in and the one a person can compare against the workflow's declared
 * inputs. Objects and arrays are indented rather than flattened: the alternative
 * is `[object Object]`, which tells nobody anything.
 *
 * Typed `unknown` rather than `JsonValue`: a workflow input is always JSON, but
 * a transcript entry's `tool_call.input` (history.txt decision 3) can be
 * whatever shape the tool declared, so the wider type is the honest one for
 * both callers — the logic here doesn't care which it received.
 */
export function formatInputValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2) ?? 'null';
}

/**
 * Which derived status a recorded run reads as.
 *
 * Four of the eleven recorded statuses are stages of getting work started rather
 * than anything a person waits to hear about, so they all read as in progress.
 *
 * The words and the colour are *not* set here — they come from
 * `STATUS_PRESENTATION`, which is the one table the board's cards and the task
 * detail already read. A second copy of "Error" and its colour living beside
 * this map is exactly how two surfaces end up disagreeing about what a status
 * is called.
 */
const RUN_OUTCOME_KIND: Record<
  TaskExecutionStatus,
  CardExecutionState['kind'] | null
> = {
  orchestrating: 'live',
  provisioning: 'live',
  submitting: 'live',
  submitted: 'live',
  running: 'live',
  completed: 'completed',
  failed: 'failed',
  blocked: 'blocked',
  // `null`, not absent. This was `Partial<Record<…>>` and listed only the eight
  // statuses that map to a card, which meant the contract could grow a ninth and
  // nothing here would object: `stopped` was added for spec 078 and every task
  // with a stopped run crashed the executions panel on `outcome.colourClass`,
  // because `runOutcome` returned `undefined` for it. A total `Record` makes the
  // *next* status a type error in this file rather than a crash in front of
  // someone, which is the only reason the three nulls below are written out.
  initial: null,
  skipped: null,
  stopped: 'stopped',
  // Waiting for a slot (spec 115): the card kind of the same name, so the panel
  // and the card say "Queued" from one table.
  queued: 'queued',
  // A `linked_workflow` join's run has finished dispatching and is waiting on
  // the tasks it created, same bucket as any other run still in flight (spec
  // 097) — the join's own progress is shown by the board counter (US3), not
  // by a distinct run-outcome label here.
  awaiting_children: 'live',
};

/**
 * The two outcomes with no equivalent on a card, because they are facts about a
 * *run* rather than states a task can be in: a run can be just starting or
 * skipped outright, and neither describes the task itself.
 *
 * `stopped` was briefly a third. It moved to the table above once the board
 * learned to show it (078 FR-006), which is the point of that table existing:
 * the panel and the card now take the word and the colour from one place, and
 * cannot come to disagree about what a stopped run is called.
 */
const RUN_ONLY_OUTCOME: Partial<Record<TaskExecutionStatus, RunOutcome>> = {
  // "Starting", not "Queued": `initial` is the instant between a run being asked
  // for and the admission decision. Since spec 115 a real queue exists, and one
  // word must not name two things.
  initial: { label: 'Starting', colourClass: 'text-muted-foreground' },
  skipped: { label: 'Skipped', colourClass: 'text-muted-foreground' },
};

/**
 * What a status nobody taught this file about reads as.
 *
 * Unreachable while the two tables above agree — which is exactly why it is
 * here. The type system now catches a *new* status, but not a `null` in the map
 * above with no entry beside it, and the cost of being wrong about that is a
 * component that throws rather than a run that reads oddly for one release.
 */
const UNKNOWN_OUTCOME: RunOutcome = {
  label: 'Unknown',
  colourClass: 'text-muted-foreground',
};

export interface RunOutcome {
  label: string;
  colourClass: string;
}

/** How one recorded run reads, in the vocabulary every other surface uses. */
export function runOutcome(status: TaskExecutionStatus): RunOutcome {
  const kind = RUN_OUTCOME_KIND[status];
  if (kind) {
    const presentation = STATUS_PRESENTATION[kind];
    if (presentation) {
      return {
        label: presentation.canonicalName,
        colourClass: presentation.colourClass ?? 'text-muted-foreground',
      };
    }
  }
  return RUN_ONLY_OUTCOME[status] ?? UNKNOWN_OUTCOME;
}

/**
 * How much text a tool block holds, said before the reader opens it (spec 096 FR-006).
 *
 * Bytes rather than lines: a one-line JSON blob and a thousand-line log are both "one
 * entry", and bytes are the honest measure of what opening the block will put on the
 * screen. One decimal above bytes, the way file managers do it.
 */
export function formatByteSize(bytes: number): string {
  const KILO = 1024;
  if (bytes < KILO) return `${bytes} B`;
  if (bytes < KILO * KILO) return `${(bytes / KILO).toFixed(1)} KB`;
  return `${(bytes / (KILO * KILO)).toFixed(1)} MB`;
}

/** Bytes of a string as UTF-8 — what `formatByteSize` should be handed for text. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * A run's turn count against its limit (spec 102), as "count/limit" — or `null`
 * when the limit isn't known, so a caller shows nothing rather than a fabricated
 * denominator. Both figures are terminal-state data (per backend spec 098): a run
 * still executing has neither, and this function draws no distinction between
 * that and a finished run whose limit was never recorded — both read as unknown.
 */
export function formatTurns(
  numTurns: number | null | undefined,
  maxTurns: number | null | undefined,
): string | null {
  if (numTurns == null || maxTurns == null) return null;
  return `${String(numTurns)}/${String(maxTurns)}`;
}

/**
 * A run's cost in the currency it was recorded in (spec 096 FR-017).
 *
 * `COST_FORMAT` assumes the default currency, which every run had until currencies
 * became per-run data; this keeps that exact output for the default and switches the
 * currency only when a run names another, so existing displays do not move.
 */
export function formatCost(
  cost: string | number,
  currency?: string | null,
): string {
  const amount = typeof cost === 'string' ? Number(cost) : cost;
  // A figure the contract typed as a string and that does not parse is shown as
  // it came, not as `$NaN` dressed up as money.
  if (!Number.isFinite(amount)) return String(cost);
  if (!currency || currency === DEFAULT_CURRENCY)
    return COST_FORMAT.format(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}
